import React, { useEffect, useState } from 'react';
import type { FC } from 'react';
import {
  Grid,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box
} from '@mui/material';
import type { UseTranslationResponse } from 'react-i18next';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import type { UseFormGetValues } from 'react-hook-form';
import { Table, Column } from 'react-virtualized';
import moment from 'moment';
import type { Moment } from 'moment';
import styles from './DailyTimeReportTable.module.scss';
import tableStyles from '../../ContactListings/ContactListingsTable/ContactListingsTable.module.scss';
import FormInputSelect from '../../forms/FormInputSelect';
import FormInputTextField from '../../forms/FormInputTextField';
import FormMaskInputTextField from '../../forms/FormMaskInputTextField';
import type { OptionProps } from '../../../models/OptionProps';
import type { DailyTimeReportPerson } from '../../../models/DailyTimeReport';
import scrollStyles from '../../../theme/sidebar.module.scss';
import { useAppSelector } from '../../../state/hooks';
import type { RootState } from '../../../state/store';
import NothingToSee, { NothingToSeeEntityType } from '../../nothingToSee/NothingToSee';
import type { DTRButtonsForm } from '../DailyTimeReport';
import DTRService from '../../../services/DTRService';

const rowHeight: number = 60;
const tableHeaderHeight: number = 60;
export const textAreaHeight: number = 90;
let latestInputTime: null | Moment = null;

export interface DailyTimeReportTableProps {
  tableWidth: number
  tableHeight: number
  showTravelTime: boolean
  showSecondMeal: boolean
  getValues: UseFormGetValues<DTRButtonsForm>
}

const DailyTimeReportTable: FC<DailyTimeReportTableProps> = ({
  tableHeight,
  tableWidth,
  showTravelTime,
  showSecondMeal,
  getValues
}: DailyTimeReportTableProps) => {
  const { t }: UseTranslationResponse<'translation', undefined> = useTranslation();

  const reportPersons: DailyTimeReportPerson[] = useAppSelector((state: RootState) => state.dailyTimeReport.reportPersons);
  const reportNote: string = useAppSelector((state: RootState) => state.dailyTimeReport.reportNote);

  const [tableReportPersons, setTableReportPersons] = useState<DailyTimeReportPerson[]>([]);
  const [noteContent, setNoteContent] = useState<string>('');

  useEffect(() => {
    const reportPersonValues: any = {};
    reportPersons.forEach((reportPerson: DailyTimeReportPerson) => {
      Object.keys(reportPerson).forEach((key: string) => {
        reportPersonValues[`${key}$$${reportPerson.id}`] = reportPerson[key as keyof DailyTimeReportPerson];
      });
    });
    reset({ ...control._formValues, ...reportPersonValues });
    setTableReportPersons(reportPersons);
  }, [reportPersons]);

  useEffect(() => {
    if (reportNote && reportNote !== noteContent) {
      setNoteContent(reportNote);
    }
  }, [reportNote]);

  const { control, reset } = useForm({
    defaultValues: {
      call_type: 'weekly',
      call_time: '',
      travel_start_time: '',
      travel_end_time: '',
      first_meal_start_time: '',
      first_meal_end_time: '',
      second_meal_start_time: '',
      second_meal_end_time: '',
      kit_time: '',
      upgrade_time: '',
      wrap_time: ''
    }
  })

  const statusOptions: OptionProps[] = [{
    key: 'weekly',
    value: 'weekly',
    label: 'dashboard.DTR.table.options.status.weekly'
  }, {
    key: 'daily',
    value: 'daily',
    label: 'dashboard.DTR.table.options.status.daily'
  }, {
    key: 'not-in',
    value: 'not-in',
    label: 'dashboard.DTR.table.options.status.notIn'
  }];

  const handleNoteChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    const newNote: string = event.target.value;
    setNoteContent(newNote);
    const department: string = getValues('department');
    const inputTime: Moment = moment();
    latestInputTime = inputTime;
    setTimeout(function () {
      if (latestInputTime?.valueOf() === inputTime.valueOf()) {
        void DTRService.updateNote(department, newNote);
      }
    }, 750);
  }

  const handleStatusChange = (value: string, memberId: string): void => {
    setTableReportPersons((prevPersons: DailyTimeReportPerson[]) =>
      prevPersons.map((person: DailyTimeReportPerson) => person.id === memberId ? { ...person, call_type: value } : person)
    );
    void DTRService.saveDailyTimeLogs({
      personIds: memberId,
      call_type: value
    })
  }

  const handleValueChange = (property: string, value: string, memberId: string): void => {
    console.log(value);
    setTableReportPersons((prevPersons: DailyTimeReportPerson[]) =>
      prevPersons.map((person: DailyTimeReportPerson) => person.id === memberId ? { ...person, [property]: value } : person)
    );
    void DTRService.saveDailyTimeLogs({
      personIds: memberId,
      [property]: value
    });
  }

  const getTimeColumnWidth = (width: number): number => {
    let columnWidthPercentage: number = 0.1;
    if (showTravelTime && showSecondMeal) {
      columnWidthPercentage = 0.075;
    } else if (showTravelTime || showSecondMeal) {
      columnWidthPercentage = 0.0875;
    }
    return width * columnWidthPercentage;
  }

  const getAlphaColumnWidth = (width: number): number => {
    let columnWidthPercentage: number = 0.2;
    if (showTravelTime && showSecondMeal) {
      columnWidthPercentage = 0.125;
    } else if (showTravelTime || showSecondMeal) {
      columnWidthPercentage = 0.15;
    }
    return width * columnWidthPercentage;
  }

  const getTimeCellWidth = (): string => {
    let getWidthPercentage: string = '10%';
    if (showTravelTime && showSecondMeal) {
      getWidthPercentage = '7.5%';
    } else if (showTravelTime || showSecondMeal) {
      getWidthPercentage = '8.75%';
    }
    return getWidthPercentage;
  }

  const getAlphaCellWidth = (): string => {
    let getWidthPercentage: string = '20%';
    if (showTravelTime && showSecondMeal) {
      getWidthPercentage = '12.5%';
    } else if (showTravelTime || showSecondMeal) {
      getWidthPercentage = '15%';
    }
    return getWidthPercentage;
  }

  const getEmptyColWidth = (): number => {
    let colWidth: number = 8;
    if (showTravelTime && showSecondMeal) {
      colWidth = 12;
    } else if (showTravelTime || showSecondMeal) {
      colWidth = 10;
    }
    return colWidth;
  }

  const rowRenderer = ({ index, key, style }: { index: number, key: string, style: any }) => {
    const rowData: DailyTimeReportPerson = tableReportPersons[index];
    const isNotIn: boolean = (rowData.call_type === 'not-in');
    return (
      <div
        style={{
          ...style,
          width: '100%'
        }}
        className={styles.tableRow}
        key={`row-${key}`}
      >
        <div
          style={{ width: getTimeCellWidth() }}
          className={`${styles.tableCell} ${styles.nameCell}`}
        >
          <Box className={styles.nameBox}>{rowData.name}</Box>
          <Box className={styles.nameBox}>{rowData.position}</Box>
        </div>
        <div
            style={{ width: getTimeCellWidth() }}
            className={styles.tableCell}
        >
          <FormInputSelect
            centered
            name={`call_type$$${rowData.id}`}
            options={statusOptions.map((option: OptionProps) => ({ ...option, label: t(option.label) }))}
            onItemClick={(value: string) => handleStatusChange(value, rowData.id)}
            formItemProps={{
              control
            }}
            width={'100px'}
          />
        </div>
        {(showTravelTime)
          ? <div
              style={{ width: getTimeCellWidth() }}
              className={styles.tableCell}
            >
              {(isNotIn)
                ? <Box>&nbsp;</Box>
                : <FormMaskInputTextField
                  id={`travel_start_time-${rowData.id}`}
                  name={`travel_start_time$$${rowData.id}`}
                  formItemProps={{
                    control
                  }}
                  onValueChanged={(value: string) => handleValueChange('travel_start_time', value, rowData.id)}
                />
              }
            </div>
          : <></>
        }
        <div
            style={{ width: getTimeCellWidth() }}
            className={styles.tableCell}
        >
          {(isNotIn)
            ? <Box>&nbsp;</Box>
            : <FormMaskInputTextField
                id={`call_time-${rowData.id}`}
                name={`call_time$$${rowData.id}`}
                formItemProps={{
                  control
                }}
                onValueChanged={(value: string) => handleValueChange('call_time', value, rowData.id)}
            />
          }
        </div>
        <div
            style={{ width: getTimeCellWidth() }}
            className={styles.tableCell}
        >
          {(isNotIn)
            ? <Box>&nbsp;</Box>
            : <FormMaskInputTextField
                id={`first_meal_start_time-${rowData.id}`}
                name={`first_meal_start_time$$${rowData.id}`}
                formItemProps={{
                  control
                }}
                onValueChanged={(value: string) => handleValueChange('first_meal_start_time', value, rowData.id)}
            />
          }
        </div>
        <div
            style={{ width: getTimeCellWidth() }}
            className={styles.tableCell}
        >
          {(isNotIn)
            ? <Box>&nbsp;</Box>
            : <FormMaskInputTextField
                id={`first_meal_end_time-${rowData.id}`}
                name={`first_meal_end_time$$${rowData.id}`}
                formItemProps={{
                  control
                }}
                onValueChanged={(value: string) => handleValueChange('first_meal_end_time', value, rowData.id)}
            />
          }
        </div>
        {(showSecondMeal)
          ? <div
              style={{ width: getTimeCellWidth() }}
              className={styles.tableCell}
            >
              {(isNotIn)
                ? <Box>&nbsp;</Box>
                : <FormMaskInputTextField
                    id={`second_meal_start_time-${rowData.id}`}
                    name={`second_meal_start_time$$${rowData.id}`}
                    formItemProps={{
                      control
                    }}
                    onValueChanged={(value: string) => handleValueChange('second_meal_start_time', value, rowData.id)}
                />
              }
            </div>
          : <></>
        }
        {(showSecondMeal)
          ? <div
            style={{ width: getTimeCellWidth() }}
            className={styles.tableCell}
          >
            {(isNotIn)
              ? <Box>&nbsp;</Box>
              : <FormMaskInputTextField
                  id={`second_meal_end_time-${rowData.id}`}
                  name={`second_meal_end_time$$${rowData.id}`}
                  formItemProps={{
                    control
                  }}
                  onValueChanged={(value: string) => handleValueChange('second_meal_end_time', value, rowData.id)}
              />
            }
          </div>
          : <></>
        }
        <div
          style={{ width: getTimeCellWidth() }}
          className={styles.tableCell}
        >
          {(isNotIn)
            ? <Box>&nbsp;</Box>
            : <FormMaskInputTextField
                id={`wrap_time-${rowData.id}`}
                name={`wrap_time$$${rowData.id}`}
                formItemProps={{
                  control
                }}
                onValueChanged={(value: string) => handleValueChange('wrap_time', value, rowData.id)}
            />
          }
        </div>
        {(showTravelTime)
          ? <div
            style={{ width: getTimeCellWidth() }}
            className={styles.tableCell}
          >
            {(isNotIn)
              ? <Box>&nbsp;</Box>
              : <FormMaskInputTextField
                  id={`travel_end_time-${rowData.id}`}
                  name={`travel_end_time$$${rowData.id}`}
                  formItemProps={{
                    control
                  }}
                  onValueChanged={(value: string) => handleValueChange('travel_end_time', value, rowData.id)}
              />
            }
          </div>
          : <></>
        }
        <div
            style={{ width: getAlphaCellWidth() }}
            className={styles.tableCell}
        >
          {(isNotIn)
            ? <Box>&nbsp;</Box>
            : <FormInputTextField
                id={`kit_time-${rowData.id}`}
                name={`kit_time$$${rowData.id}`}
                formItemProps={{
                  control
                }}
                onValueChanged={(value: string) => handleValueChange('kit_time', value, rowData.id)}
                width={'90%'}
                center={true}
            />
          }
        </div>
        <div
            style={{ width: getAlphaCellWidth() }}
            className={styles.tableCell}
        >
          {(isNotIn)
            ? <Box>&nbsp;</Box>
            : <FormInputTextField
              id={`upgrade_time-${rowData.id}`}
              name={`upgrade_time$$${rowData.id}`}
              formItemProps={{
                control
              }}
              onValueChanged={(value: string) => handleValueChange('upgrade_time', value, rowData.id)}
              width={'90%'}
              center={true}
            />
          }
        </div>
      </div>
    );
  }

  return (
    <Grid
      item
      container
      sx={{
        width: tableWidth
      }}
      className={styles.DailyTimeReportTable}
    >
      <Grid
        item
        sx={{
          maxHeight: tableHeight,
          width: tableWidth
        }}
      >
        {(tableReportPersons.length > 0)
          ? <Table
            rowHeight={rowHeight}
            headerHeight={tableHeaderHeight}
            height={tableHeight}
            width={tableWidth}
            rowCount={tableReportPersons.length}
            rowGetter={({ index }) => tableReportPersons[index]}
            rowClassName={tableStyles.tableRow}
            headerRowRenderer={({ columns, style }) => (
              <div style={{ ...style, paddingRight: 0 }} className={tableStyles.headerRow}>
                <div className={tableStyles.headerContainer}>
                  {columns}
                </div>
              </div>
            )}
            gridClassName={scrollStyles.scrollbar}
            className={styles.tableBox}
            style={{
              height: tableHeight
            }}
            rowRenderer={rowRenderer}
          >
            <Column
              label={''}
              dataKey={'id'}
              width={getTimeColumnWidth(tableWidth)}
              className={tableStyles.tableCell}
              headerClassName={`${styles.headerCell}`}
              headerRenderer={() => {
                return (
                  <div>
                    &nbsp;
                  </div>
                );
              }}
            />
            <Column
              label={t('dashboard.DTR.table.headers.status')}
              dataKey={'call_type'}
              width={getTimeColumnWidth(tableWidth)}
              className={tableStyles.tableCell}
              headerClassName={`${styles.headerCell}`}
              headerRenderer={({ label }) => {
                return (
                  <div>
                    <Box component={'span'}>{label}</Box>
                  </div>
                );
              }}
            />
            {(showTravelTime) && <Column
              label={t('dashboard.DTR.table.headers.travelStart')}
              dataKey={'travel_start_time'}
              width={getTimeColumnWidth(tableWidth)}
              className={tableStyles.tableCell}
              headerClassName={`${styles.headerCell}`}
              headerRenderer={({ label }) => {
                return (
                  <div>
                    <Box component={'span'}>{label}</Box>
                  </div>
                );
              }}
              />
            }
            <Column
              label={t('dashboard.DTR.table.headers.callTime')}
              dataKey={'call_time'}
              width={getTimeColumnWidth(tableWidth)}
              className={tableStyles.tableCell}
              headerClassName={`${styles.headerCell}`}
              headerRenderer={({ label }) => {
                return (
                  <div>
                    <Box component={'span'}>{label}</Box>
                  </div>
                );
              }}
            />
            <Column
              label={t('dashboard.DTR.table.headers.mealStart')}
              dataKey={'first_meal_start_time'}
              width={getTimeColumnWidth(tableWidth)}
              className={tableStyles.tableCell}
              headerClassName={`${styles.headerCell}`}
              headerRenderer={({ label }) => {
                return (
                  <div>
                    <Box component={'span'}>{label}</Box>
                  </div>
                );
              }}
            />
            <Column
              label={t('dashboard.DTR.table.headers.mealFinish')}
              dataKey={'first_meal_end_time'}
              width={getTimeColumnWidth(tableWidth)}
              className={tableStyles.tableCell}
              headerClassName={`${styles.headerCell}`}
              headerRenderer={({ label }) => {
                return (
                  <div>
                    <Box component={'span'}>{label}</Box>
                  </div>
                );
              }}
            />
            {(showSecondMeal) && <Column
              label={t('dashboard.DTR.table.headers.secondMealStart')}
              dataKey={'second_meal_start_time'}
              width={getTimeColumnWidth(tableWidth)}
              className={tableStyles.tableCell}
              headerClassName={`${styles.headerCell}`}
              headerRenderer={({ label }) => {
                return (
                  <div>
                    <Box component={'span'}>{label}</Box>
                  </div>
                );
              }}
            />}
            {(showSecondMeal) && <Column
              label={t('dashboard.DTR.table.headers.secondMealFinish')}
              dataKey={'second_meal_end_time'}
              width={getTimeColumnWidth(tableWidth)}
              className={tableStyles.tableCell}
              headerClassName={`${styles.headerCell}`}
              headerRenderer={({ label }) => {
                return (
                  <div>
                    <Box component={'span'}>{label}</Box>
                  </div>
                );
              }}
            />}
            <Column
              label={t('dashboard.DTR.table.headers.wrap')}
              dataKey={'wrap_time'}
              width={getTimeColumnWidth(tableWidth)}
              className={tableStyles.tableCell}
              headerClassName={`${styles.headerCell}`}
              headerRenderer={({ label }) => {
                return (
                  <div>
                    <Box component={'span'}>{label}</Box>
                  </div>
                );
              }}
            />
            {(showTravelTime) && <Column
              label={t('dashboard.DTR.table.headers.travelFinish')}
              dataKey={'travel_end_time'}
              width={getTimeColumnWidth(tableWidth)}
              className={tableStyles.tableCell}
              headerClassName={`${styles.headerCell}`}
              headerRenderer={({ label }) => {
                return (
                  <div>
                    <Box component={'span'}>{label}</Box>
                  </div>
                );
              }}
            />}
            <Column
              label={t('dashboard.DTR.table.headers.kit')}
              dataKey={'kit_time'}
              width={getAlphaColumnWidth(tableWidth)}
              className={tableStyles.tableCell}
              headerClassName={`${styles.headerCell}`}
              headerRenderer={({ label }) => {
                return (
                  <div>
                    <Box component={'span'}>{label}</Box>
                  </div>
                );
              }}
            />
            <Column
              label={t('dashboard.DTR.table.headers.upgrade')}
              dataKey={'upgrade_time'}
              width={getAlphaColumnWidth(tableWidth)}
              className={tableStyles.tableCell}
              headerClassName={`${styles.headerCell}`}
              headerRenderer={({ label }) => {
                return (
                  <div>
                    <Box component={'span'}>{label}</Box>
                  </div>
                );
              }}
            />
          </Table>
          : <MuiTable
              className={`${styles.DailyTimeReportTable} ${styles.emptyTable}`}
              sx={{
                width: tableWidth,
                height: tableHeight
              }}
          >
            <TableHead className={styles.tableHeader}>
              <TableRow className={styles.headerRow}>
                <TableCell className={styles.headerCell}>&nbsp;</TableCell>
                <TableCell className={styles.headerCell}>
                  {t('dashboard.DTR.table.headers.status')}
                </TableCell>
                {(showTravelTime)
                  ? <TableCell className={styles.headerCell}>
                    {t('dashboard.DTR.table.headers.travelStart')}
                  </TableCell>
                  : <></>
                }
                <TableCell className={styles.headerCell}>
                  {t('dashboard.DTR.table.headers.callTime')}
                </TableCell>
                <TableCell className={styles.headerCell}>
                  {t('dashboard.DTR.table.headers.mealStart')}
                </TableCell>
                <TableCell className={styles.headerCell}>
                  {t('dashboard.DTR.table.headers.mealFinish')}
                </TableCell>
                {(showSecondMeal)
                  ? <TableCell className={styles.headerCell}>
                    {t('dashboard.DTR.table.headers.secondMealStart')}
                  </TableCell>
                  : <></>
                }
                {(showSecondMeal)
                  ? <TableCell className={styles.headerCell}>
                    {t('dashboard.DTR.table.headers.secondMealFinish')}
                  </TableCell>
                  : <></>
                }
                <TableCell className={styles.headerCell}>
                  {t('dashboard.DTR.table.headers.wrap')}
                </TableCell>
                {(showTravelTime)
                  ? <TableCell className={styles.headerCell}>
                    {t('dashboard.DTR.table.headers.travelFinish')}
                  </TableCell>
                  : <></>
                }
                <TableCell className={styles.headerCell}>
                  {t('dashboard.DTR.table.headers.kit')}
                </TableCell>
                <TableCell className={styles.headerCell}>
                  {t('dashboard.DTR.table.headers.upgrade')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableCell colSpan={getEmptyColWidth()}>
              <NothingToSee entityType={NothingToSeeEntityType.DTR}/>
              </TableCell>
            </TableBody>
          </MuiTable>
        }
      </Grid>
      <Grid
        container
        item
        sx={{
          height: `${textAreaHeight}px`,
          width: tableWidth
        }}
        className={styles.textAreaContainer}
      >
        <Grid
         item
         className={styles.textAreaLabel}
         sx={{
           width: getTimeCellWidth()
         }}
        >
          <Box>{t('dashboard.DTR.table.headers.departmentNotes')}</Box>
        </Grid>
        <Grid item className={styles.textAreaBox}><textarea onChange={handleNoteChange} value={noteContent}></textarea></Grid>
      </Grid>
    </Grid>
  );
}

export default DailyTimeReportTable;
